import { ColDef, GridApi } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface DataTableProps<DataType> {
    columns: DataTableColumnDef<DataType>[];
    data: DataType[]|null;
    onRowSelected?: (data: DataType) => void;
    rowClassname?: (data: DataType) => string;
    height?: number|string;
    rowHeight?: number;
    search?: string;
}

export interface DataTableColumnDef<DataType> {
    headerName: string;
    flex?: number;
    field?: string;
    valueGetter?: (props: {data: DataType}) => any;
    cellRenderer?: (params: {value: any}) => React.ReactNode;
    sortable?: boolean;
    comparator?: (row1: {data: DataType}, row2: {data: DataType}, desc: boolean) => number | undefined;
    sort?: 'asc'|'desc';
    quickFilterText?: (data: DataType) => string;
}


export const DataTable = <DataType, >({columns, data, search, onRowSelected, rowClassname, height, rowHeight}: DataTableProps<DataType>) => {
    const [loading, setLoading] = useState(false);
    const [gridAPI, setGridAPI] = useState<GridApi<DataType>|null>(null);
    const { t } = useTranslation();

    const columnDefs = useMemo<ColDef[]>(() => columns.map((c, idx) => ({
        headerName: c.headerName,
        field: c.field,
        valueGetter: c.valueGetter,
        flex: c.flex,
        cellRenderer: c.cellRenderer,
        resizable: idx < columns.length - 1,
        comparator: !c.comparator ? undefined : ((val1, val2, node1, node2, desc) => c.comparator ? c.comparator({data: val1}, {data: val2}, desc) : undefined),
        sortable: c.sortable ?? true,
        sort: c.sort,
        getQuickFilterText: params => c.quickFilterText ? c.quickFilterText(params.data) : undefined
    }) as ColDef), [columns]);

    useEffect(() => {
        setLoading(data == null);
    }, [data]);

    useEffect(() => {
        if (!gridAPI) return;
        if (loading) gridAPI.showLoadingOverlay();
        else gridAPI.hideOverlay();
    }, [loading, gridAPI]);

    useEffect(() => {
        gridAPI?.setGridOption('quickFilterText', search);
    }, [search]);

    return (
        <div
            className="w-100 ag-theme-material position-relative"
            style={{height: height || '100%'}}
        >
            <AgGridReact<DataType>
                rowData={data}
                animateRows={true}
                columnDefs={columnDefs}
                rowSelection="single"
                onRowSelected={!onRowSelected ? undefined : p => {
                    if (p.node.isSelected() && p.data) onRowSelected(p.data);
                }}
                getRowClass={!rowClassname ? undefined : p => (p.data ? rowClassname(p.data) : undefined)}
                rowHeight={rowHeight}
                onGridReady={props => setGridAPI(props.api)}
                overlayLoadingTemplate={t('loading...')}
                suppressScrollOnNewData={true}
            />
        </div>
    )
}

export const DataTableStringComparison = (s1: string, s2: string): number => {
    if (s1 == s2) return 0;
    return (s1 > s2) ? 1 : -1;
}

export const DataTableDateComparison = (s1: Date, s2: Date): number => {
    return s1.getTime() - s2.getTime();
}